import * as React from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useQuerySubscriptionPlans } from '../api/useQuerySubscriptionPlans'
import { useQueryTenantMe } from '../api/useQueryTenantMe'
import { useMutationCreateCheckout } from '../api/useMutationCreateCheckout'
import { PlanCard } from '../components/PlanCard'
import { PlanPickerSkeleton } from '../components/PlanPickerSkeleton'
import { PaymentMethodsStrip } from '../components/PaymentMethodsStrip'
import { recommendPlan } from '../constants/plans'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TermsAcceptance } from '@/modules/legal'
import type { BillingMode } from '../types'

// Przelewy24 = one-off prepaid only for now. The recurring monthly-card mode
// MonthlyCard = recurring card-on-file (auto-renew); requires P24 card one-click
// enabled on the merchant account.
const MODE_TABS: { value: BillingMode; labelKey: string; badge?: string }[] = [
  { value: 'Prepaid6m', labelKey: 'subscription.checkout.mode6m', badge: '−10%' },
  { value: 'Prepaid12m', labelKey: 'subscription.checkout.mode12m', badge: '−20%' },
  { value: 'MonthlyCard', labelKey: 'subscription.checkout.modeMonthlyCard' },
]

const MODE_HELPER_KEY: Record<BillingMode, string> = {
  MonthlyCard: 'subscription.checkout.helperMonthlyCard',
  Prepaid6m: 'subscription.checkout.helperPrepaid6m',
  Prepaid12m: 'subscription.checkout.helperPrepaid12m',
}

export const PlanPickerPage: React.FC = () => {
  const { t } = useLocalize()
  const [searchParams] = useSearchParams()
  const queryCompanyId = searchParams.get('companyId')
  const { data: tenant, isLoading: tenantLoading } = useQueryTenantMe()
  const companyId = queryCompanyId ?? tenant?.companyId ?? null

  const { data: plansData, isLoading: plansLoading } = useQuerySubscriptionPlans()
  const checkout = useMutationCreateCheckout(companyId ?? '')

  const [driverCount, setDriverCount] = React.useState(1)
  const [mode, setMode] = React.useState<BillingMode>('Prepaid6m')
  const [selectedCode, setSelectedCode] = React.useState<string | null>(null)
  const [saveCard, setSaveCard] = React.useState(false)
  const [termsAccepted, setTermsAccepted] = React.useState(false)

  const recommendedCode = React.useMemo(
    () => (plansData ? recommendPlan(driverCount, plansData.plans) : null),
    [driverCount, plansData],
  )

  React.useEffect(() => {
    if (!selectedCode && recommendedCode) setSelectedCode(recommendedCode)
  }, [recommendedCode, selectedCode])

  if (plansLoading || !plansData || tenantLoading) {
    return <PlanPickerSkeleton />
  }

  if (!companyId) {
    return <Navigate to="/post-register" replace />
  }

  const goToCheckout = async () => {
    if (!selectedCode || !termsAccepted) return
    const res = await checkout.mutateAsync({
      planCode: selectedCode,
      billingMode: mode,
      driverCount,
      saveCard,
    })
    window.location.href = res.url
  }

  return (
    <div className="tw-bg-content-primary tw-min-h-screen tw-py-8 tw-px-4">
      <div className="tw-max-w-5xl tw-mx-auto tw-bg-white tw-border tw-border-base-300 tw-rounded-lg tw-p-6 sm:tw-p-8">
        <header className="tw-mb-8">
          <h1 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-content-base tw-m-0">
            {t('subscription.checkout.title')}
          </h1>
          <p className="tw-text-sm tw-text-content-base/70 tw-mt-2 tw-mb-0">
            {t('subscription.checkout.subtitle')}
          </p>
        </header>

        <section className="tw-mb-6">
          <label
            htmlFor="driver-count"
            className="tw-block tw-text-sm tw-font-medium tw-text-content-base tw-mb-2"
          >
            {t('subscription.checkout.driverCount')}
          </label>
          <div className="tw-inline-flex tw-items-stretch tw-rounded-lg tw-border tw-border-base-300 tw-overflow-hidden">
            <button
              type="button"
              onClick={() => setDriverCount((c) => Math.max(1, c - 1))}
              className="tw-px-3 tw-text-lg tw-font-semibold tw-text-content-base hover:tw-bg-base-200 tw-cursor-pointer"
              aria-label={t('subscription.checkout.decrease')}
            >
              −
            </button>
            <input
              id="driver-count"
              type="number"
              min={1}
              max={1000}
              value={driverCount}
              onChange={(e) =>
                setDriverCount(Math.max(1, Math.min(1000, Number(e.target.value) || 1)))
              }
              className="tw-w-20 tw-text-center tw-border-x tw-border-base-300 tw-bg-white tw-text-sm tw-font-medium focus:tw-outline-none"
            />
            <button
              type="button"
              onClick={() => setDriverCount((c) => Math.min(1000, c + 1))}
              className="tw-px-3 tw-text-lg tw-font-semibold tw-text-content-base hover:tw-bg-base-200 tw-cursor-pointer"
              aria-label={t('subscription.checkout.increase')}
            >
              +
            </button>
          </div>
        </section>

        <section className="tw-mb-8">
          <div className="tw-block tw-text-sm tw-font-medium tw-text-content-base tw-mb-2">
            {t('subscription.checkout.billingMode')}
          </div>
          <div
            role="tablist"
            aria-label={t('subscription.checkout.billingMode')}
            className="tw-inline-flex tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-200/50 tw-p-1 tw-gap-1"
          >
            {MODE_TABS.map((tab) => {
              const active = mode === tab.value
              return (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setMode(tab.value)}
                  className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 sm:tw-px-4 tw-py-1.5 tw-rounded tw-text-sm tw-font-medium tw-transition-colors tw-cursor-pointer ${
                    active
                      ? 'tw-bg-white tw-text-primary tw-shadow-sm'
                      : 'tw-text-content-base/70 hover:tw-text-content-base'
                  }`}
                >
                  {t(tab.labelKey)}
                  {tab.badge && (
                    <span
                      className={`tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded ${
                        active ? 'tw-bg-success/10 tw-text-success' : 'tw-bg-base-300 tw-text-content-base/70'
                      }`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          <p className="tw-text-xs tw-text-content-base/60 tw-mt-2 tw-max-w-2xl">
            {t(MODE_HELPER_KEY[mode])}
          </p>
        </section>

        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 tw-mb-8">
          {plansData.plans.map((plan) => (
            <PlanCard
              key={plan.code}
              plan={plan}
              selectedMode={mode}
              driverCount={driverCount}
              recommended={plan.code === recommendedCode}
              selected={plan.code === selectedCode}
              onSelect={() => setSelectedCode(plan.code)}
            />
          ))}
        </div>

        {checkout.error && (
          <div className="tw-mb-4 tw-rounded tw-border tw-border-error/40 tw-bg-error/5 tw-text-error tw-text-sm tw-px-4 tw-py-3">
            {t('subscription.checkout.checkoutError')}
          </div>
        )}

        <label className="tw-mb-4 tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-base-content/80 tw-cursor-pointer">
          <input
            type="checkbox"
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
            className="tw-mt-0.5"
          />
          <span>
            Zapisz kartę do automatycznych płatności (odnawianie pakietu i dopłaty za
            dodatkowych kierowców). Możesz ją później usunąć w ustawieniach.
          </span>
        </label>

        <div className="tw-border-t tw-border-base-300 tw-pt-5">
          {/* Akceptacja regulaminu bezpośrednio przy przycisku płatności —
              w tym samym kroku, w którym użytkownik finalizuje zakup. */}
          <div className="tw-mb-4 tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-200/40 tw-px-4 tw-py-3">
            <TermsAcceptance
              checked={termsAccepted}
              onChange={setTermsAccepted}
              template={t('subscription.checkout.acceptTerms')}
              termsLabel={t('subscription.checkout.termsLabel')}
              privacyLabel={t('subscription.checkout.privacyLabel')}
              className="tw-text-base-content/80"
            />
            <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-text-content-base/60">
              {t('subscription.checkout.activationNote')}
            </p>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
            <PaymentMethodsStrip mode={mode} />

            <button
              type="button"
              disabled={!selectedCode || !termsAccepted || checkout.isPending}
              onClick={goToCheckout}
              className="tw-px-5 tw-py-2.5 tw-rounded-lg tw-bg-primary tw-text-white tw-text-sm tw-font-semibold tw-cursor-pointer hover:tw-bg-primary/90 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-whitespace-nowrap"
            >
              {checkout.isPending
                ? t('subscription.checkout.payRedirecting')
                : t('subscription.checkout.payAction')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
